import { AppState } from 'pages';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  boardState,
  BoardStatus,
  setDeleteIndex,
  setCancelIndex,
  setStatus,
  setAlertMessage,
  setCheckIndex
} from 'common/modules/app/board';
import { LendingUserBoard } from '../components/LendingUserBoard';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import {
  GET_LENDING_USER_ID,
  UsageSituationState,
  LendingUserInformation
} from 'common/modules/domain/lendingUser';
import { PurchaseInitialState } from 'common/modules/domain/purchase';
import { Glossary, BoardGL } from 'common/utils/glossary';
import { GlossaryState } from 'common/modules/app/glossaries';
import { UserInformation } from 'common/modules/domain/user';

const _convertAccounts = (
  accounts: UsageSituationState,
  purchaseId: string,
  onClickCheckBox: (
    index: string,
    status: string,
    usage: number,
    lendingUser: LendingUserInformation | null
  ) => () => void
) => {
  if (purchaseId in accounts) {
    // キーをリストで取得
    const keyList = Object.keys(accounts[purchaseId]).sort();
    const accountList = [];
    for (const key of keyList) {
      const data = accounts[purchaseId][key];
      accountList.push({
        lendingUser: data.lendingUser,
        index: data.index,
        status: data.status,
        usage: data.usage,
        limit: data.limit,
        onClickCheckBox: onClickCheckBox(
          data.index,
          data.status,
          data.usage,
          data.lendingUser
        )
      });
    }
    return accountList;
  }
  return [];
};
/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const UsageSituationState = useSelector<AppState, UsageSituationState>(
    (state) => state.domain.lendingUser
  );
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const purchaseState = useSelector<AppState, PurchaseInitialState>(
    (state) => state.domain.purchase
  );

  const isLendingUserProcessing =
    fetchState[GET_LENDING_USER_ID] !== null &&
    fetchState[GET_LENDING_USER_ID] === FetchStatus.PROCESSING;
  const isLendingUserProcessingFail =
    fetchState[GET_LENDING_USER_ID] !== null &&
    fetchState[GET_LENDING_USER_ID] === FetchStatus.FAILED;
  const isDisplaySub =
    boardState.status === BoardStatus.ADD_LENDING_USER_DISP ||
    boardState.status === BoardStatus.COMPLETION_ADD_LENDING_USER_DISP ||
    boardState.status === BoardStatus.DELETE_LENDING_USER_DISP ||
    boardState.status === BoardStatus.COMPLETION_DELETE_LENDING_USER_DISP ||
    boardState.status === BoardStatus.BEFORE_INDIVIDUAL_CANCEL_DISP ||
    boardState.status === BoardStatus.COMPLETION_BEFORE_INDIVIDUAL_CANCEL_DISP;

  // 有償機能プランか否かのフラグ
  let isDeviceActivate = false;
  if (boardState.selectedPurchaseIdForLendingUser != '') {
    const purchaseInfo =
      purchaseState.purchasePlanList[
        boardState.selectedPurchaseIdForLendingUser
      ];
    if (purchaseInfo) {
      isDeviceActivate = purchaseInfo.plan.deviceLimitType == 2;
    }
  }

  return {
    UsageSituationState,
    isLendingUserProcessing,
    isLendingUserProcessingFail,
    user: userState,
    purchaseId: boardState.selectedPurchaseIdForLendingUser,
    deviceLimit: boardState.selectedDeviceLimit,
    quantity: boardState.selectedQuantity,
    purchaseName: boardState.planName,
    isDisplay: boardState.status === BoardStatus.LENDING_USER_DISP,
    isDisplaySub: isDisplaySub,
    isSelloutPlan: boardState.isSelloutPlan,
    lendingIndex: boardState.lendingIndex,
    canDelete: boardState.lendingIndex.deleteIndex.length > 0,
    canCancel: boardState.lendingIndex.cancelIndex.length > 0,
    glossary: Glossary.instance(glState),
    isDemo: boardState.isDemo,
    isDeviceActivate: isDeviceActivate
  };
};

type SelectedState = ReturnType<typeof useStateProps>;

/**
 * Actionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const _purchaseId = state.purchaseId;
  const _deviceLimit = state.deviceLimit;
  const _quantity = state.quantity;
  const _purchaseName = state.purchaseName;
  const _language = state.user.language;

  /** クローズボタンの押下処理 */
  const onClickClose = () => {
    dispatch(setStatus(BoardStatus.NOT_DISP));
  };

  /** state.○○Indexはdispatchで値が更新されないため、更新用の変数をここで宣言 */
  let _checkIndex: string[] = state.lendingIndex.checkIndex;
  let _deleteIndex: string[] = state.lendingIndex.deleteIndex;
  let _cancelIndex: string[] = state.lendingIndex.cancelIndex;

  /** チェックボックスの押下処理 */
  const onClickCheckBox = (
    index: string,
    status: string,
    usage: number,
    lendingUser: LendingUserInformation | null
  ) => () => {
    const isDeletePlan = lendingUser !== null && status != 'expired';
    const isCancelPlan = status != 'canceling' && status != 'forced_return';
    if (
      _deleteIndex.some((u) => u === index) ||
      _cancelIndex.some((u) => u === index)
    ) {
      /** 削除・解約可能のプランの場合、deleteIndex、cancelIndexからindex番号を削除 */
      if (isDeletePlan) {
        _deleteIndex = _deleteIndex.filter(function (v) {
          return ![index].includes(v);
        });
        dispatch(setDeleteIndex(_deleteIndex));
      }
      if (isCancelPlan) {
        _cancelIndex = _cancelIndex.filter(function (v) {
          return ![index].includes(v);
        });
        dispatch(setCancelIndex(_cancelIndex));
      }
    } else {
      /** 有償機能プランの場合は1件でも使用されていた場合(usageが0ではない)追加はしない */
      if (!(state.isDeviceActivate && usage > 0)) {
        /** 削除・解約可能のプランの場合、deleteIndex、cancelIndexにindex番号を追加 */
        if (isDeletePlan) {
          _deleteIndex = [..._deleteIndex, index];
          dispatch(setDeleteIndex(_deleteIndex));
        }
        if (isCancelPlan) {
          _cancelIndex = [..._cancelIndex, index];
          dispatch(setCancelIndex(_cancelIndex));
        }
      }
    }
    /** チェック状態を更新する */
    if (_checkIndex.some((u) => u === index)) {
      _checkIndex = _checkIndex.filter(function (v) {
        return ![index].includes(v);
      });
      dispatch(setCheckIndex(_checkIndex));
    } else {
      _checkIndex = [..._checkIndex, index];
      dispatch(setCheckIndex(_checkIndex));
    }
  };

  const convertAccounts = _convertAccounts(
    state.UsageSituationState,
    _purchaseId,
    onClickCheckBox
  );

  /** 登録済みのプラン数を算出する */
  const countLendingUser = () => {
    let lendingUser = 0;
    convertAccounts.map((account) => {
      if (
        (account.status == 'active' && account.usage > 0) ||
        (account.status == 'canceling' && account.usage > 0) ||
        account.status == 'forced_return' ||
        account.status == 'loan'
      ) {
        lendingUser += 1;
      }
    });
    return lendingUser;
  };

  /** 登録ボタンの押下処理 */
  const onClickRegister = () => {
    if (countLendingUser() >= convertAccounts.length) {
      /** 登録済みのプラン数>=プラン数(上限)の場合、アラートを表示 */
      dispatch(
        setAlertMessage(state.glossary.glx(BoardGL.pleaseDeleteLendingUser))
      );
      // 貸与ユーザー追加操作完了ボード表示
      dispatch(setStatus(BoardStatus.COMPLETION_ADD_LENDING_USER_DISP));
    } else {
      dispatch(setStatus(BoardStatus.ADD_LENDING_USER_DISP));
    }
  };

  /** レンタル停止ボタンの押下処理 */
  const onClickDelete = () => {
    dispatch(setStatus(BoardStatus.DELETE_LENDING_USER_DISP));
  };

  /** 解約ボタンの押下処理 */
  const onClickCancel = () => {
    dispatch(setStatus(BoardStatus.BEFORE_INDIVIDUAL_CANCEL_DISP));
  };

  return {
    accounts: convertAccounts,
    isDisplay: state.isDisplay,
    isDisplaySub: state.isDisplaySub,
    isLendingUserProcessing: state.isLendingUserProcessing,
    isLendingUserProcessingFail: state.isLendingUserProcessingFail,
    deviceLimit: _deviceLimit,
    quantity: _quantity,
    purchaseId: _purchaseId,
    purchaseName: _purchaseName,
    language: _language,
    isSelloutPlan: state.isSelloutPlan,
    glossary: state.glossary,
    canDelete: state.canDelete,
    canCancel: state.canCancel,
    checkedInit: _checkIndex,
    isDemo: state.isDemo,
    isDeviceActivate: state.isDeviceActivate,
    onClickClose,
    onClickRegister,
    onClickDelete,
    onClickCancel
  };
};
export default function ContainerComponent() {
  const _props = { ...useMargeProps(useStateProps()) };
  return <LendingUserBoard {..._props} />;
}
